import React, { useState, useEffect } from 'react';
import axios from 'axios';
import OrganizerLayout from '../../components/layouts/OrganizerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Calendar, Plus, Clock, Trash2, Loader2, Play, Eye, 
  Mail, FileSpreadsheet, Bell, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, '0')}:00`
}));

const OrganizerScheduledReports = () => {
  const { user, token } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [reports, setReports] = useState([]);
  const [reportTypes, setReportTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    report_type: 'daily_summary',
    frequency: 'daily',
    day_of_week: 0,
    day_of_month: 1,
    hour: 8,
    recipient_emails: '',
    is_active: true
  });

  useEffect(() => {
    fetchEvents();
    fetchReportTypes();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchReports();
    }
  }, [selectedEventId]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEventId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scheduled-reports/types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch report types:', error);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/scheduled-reports?event_id=${selectedEventId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    }
  };

  const openCreateDialog = () => {
    setFormData({
      report_type: 'daily_summary',
      frequency: 'daily',
      day_of_week: 0,
      day_of_month: 1,
      hour: 8,
      recipient_emails: user?.email || '',
      is_active: true
    });
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    const emails = formData.recipient_emails
      .split(',')
      .map(e => e.trim())
      .filter(e => e);
    
    if (emails.length === 0) {
      toast.error('Please add at least one recipient email');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/scheduled-reports`,
        {
          event_id: selectedEventId,
          report_type: formData.report_type,
          frequency: formData.frequency,
          day_of_week: formData.frequency === 'weekly' ? formData.day_of_week : null,
          day_of_month: formData.frequency === 'monthly' ? formData.day_of_month : null,
          hour: formData.hour,
          recipient_emails: emails,
          is_active: formData.is_active
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Report schedule created');
      setDialogOpen(false);
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Delete this scheduled report?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/scheduled-reports/${reportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Report schedule deleted');
      fetchReports();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleToggleActive = async (report) => {
    try {
      await axios.put(
        `${API_URL}/api/scheduled-reports/${report.id}`,
        { is_active: !report.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(report.is_active ? 'Report paused' : 'Report activated');
      fetchReports();
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const handleRunNow = async (reportId) => {
    try {
      await axios.post(
        `${API_URL}/api/scheduled-reports/${reportId}/run-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Report is being generated and will be sent shortly');
    } catch (error) {
      toast.error('Failed to run report');
    }
  };

  const handlePreview = async (reportId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/scheduled-reports/${reportId}/preview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewContent(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const formatSchedule = (report) => {
    let schedule = '';
    switch (report.frequency) {
      case 'daily':
        schedule = `Daily at ${report.hour.toString().padStart(2, '0')}:00`;
        break;
      case 'weekly':
        schedule = `Every ${DAYS_OF_WEEK.find(d => d.value === report.day_of_week)?.label || ''} at ${report.hour.toString().padStart(2, '0')}:00`;
        break;
      case 'monthly':
        schedule = `Monthly on day ${report.day_of_month} at ${report.hour.toString().padStart(2, '0')}:00`;
        break;
      default:
        schedule = report.frequency;
    }
    return schedule;
  };

  if (loading) {
    return (
      <OrganizerLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </OrganizerLayout>
    );
  }

  return (
    <OrganizerLayout>
      <div className="space-y-6" data-testid="scheduled-reports">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileSpreadsheet className="h-6 w-6 text-maroon" />
              Scheduled Reports
            </h1>
            <p className="text-gray-500">Automate email reports for your events</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedEventId} onValueChange={setSelectedEventId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select event" />
              </SelectTrigger>
              <SelectContent>
                {events.map(event => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.event_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              onClick={openCreateDialog}
              className="bg-maroon hover:bg-maroon-dark"
              data-testid="create-report-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Schedule
            </Button>
          </div>
        </div>

        {/* Reports List */}
        {reports.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No scheduled reports yet</p>
              <p className="text-gray-400 text-sm mt-2">Create automated reports to stay updated on your event</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={openCreateDialog}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Schedule
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {reports.map(report => (
              <Card key={report.id} className={`${!report.is_active ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        {report.report_type === 'daily_summary' && <FileSpreadsheet className="h-4 w-4 text-maroon" />}
                        {report.report_type === 'weekly_summary' && <Calendar className="h-4 w-4 text-maroon" />}
                        {report.report_type === 'payment_reminder' && <Bell className="h-4 w-4 text-maroon" />}
                        {reportTypes[report.report_type]?.name || report.report_type}
                        {report.is_active ? (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Paused</Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {reportTypes[report.report_type]?.description}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handlePreview(report.id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRunNow(report.id)}
                      >
                        <Play className="h-4 w-4 mr-1" />
                        Run Now
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(report.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Schedule:</span>
                      <p className="font-medium flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        {formatSchedule(report)}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Recipients:</span>
                      <p className="font-medium mt-1">
                        {report.recipient_emails?.length || 0} email(s)
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Next Run:</span>
                      <p className="font-medium mt-1">
                        {report.next_run ? new Date(report.next_run).toLocaleString() : 'Not scheduled'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Total Runs:</span>
                      <p className="font-medium mt-1">{report.run_count || 0}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="text-xs text-gray-500">
                      {report.recipient_emails?.join(', ')}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Active</span>
                      <Switch 
                        checked={report.is_active}
                        onCheckedChange={() => handleToggleActive(report)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Schedule New Report</DialogTitle>
              <DialogDescription>
                Configure automated email reports for your event
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label>Report Type</Label>
                <Select 
                  value={formData.report_type} 
                  onValueChange={(v) => setFormData({ ...formData, report_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(reportTypes).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {value.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Frequency</Label>
                <Select 
                  value={formData.frequency} 
                  onValueChange={(v) => setFormData({ ...formData, frequency: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.frequency === 'weekly' && (
                <div>
                  <Label>Day of Week</Label>
                  <Select 
                    value={formData.day_of_week.toString()} 
                    onValueChange={(v) => setFormData({ ...formData, day_of_week: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map(day => (
                        <SelectItem key={day.value} value={day.value.toString()}>
                          {day.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {formData.frequency === 'monthly' && (
                <div>
                  <Label>Day of Month</Label>
                  <Select 
                    value={formData.day_of_month.toString()} 
                    onValueChange={(v) => setFormData({ ...formData, day_of_month: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                        <SelectItem key={day} value={day.toString()}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label>Time (UTC)</Label>
                <Select 
                  value={formData.hour.toString()} 
                  onValueChange={(v) => setFormData({ ...formData, hour: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(h => (
                      <SelectItem key={h.value} value={h.value.toString()}>
                        {h.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Recipient Emails</Label>
                <Input 
                  value={formData.recipient_emails}
                  onChange={(e) => setFormData({ ...formData, recipient_emails: e.target.value })}
                  placeholder="email1@example.com, email2@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">Separate multiple emails with commas</p>
              </div>
            </div>
            
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate}
                disabled={saving}
                className="bg-maroon hover:bg-maroon-dark"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Schedule'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Report Preview</DialogTitle>
              <DialogDescription>
                {previewContent?.subject}
              </DialogDescription>
            </DialogHeader>
            
            <div className="border rounded-lg overflow-hidden mt-4">
              <div 
                className="p-4"
                dangerouslySetInnerHTML={{ __html: previewContent?.html_content || '' }}
              />
            </div>
            
            {previewContent?.report_data && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Report Data:</h4>
                <pre className="text-xs overflow-auto">
                  {JSON.stringify(previewContent.report_data, null, 2)}
                </pre>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </OrganizerLayout>
  );
};

export default OrganizerScheduledReports;
